import type {
  FairUseOverviewFilter,
  FairUseOverviewRow,
  FairUsePersonIdentity,
} from "@/api/admin/fair-use"

import { roundPercent } from "@/features/fair-use/fair-use-utils"

// =============================================================================
// Operations card ("AI budget") — non-component helpers.
//
// Split out of fair-use-utils.ts, which already carries the whole settings
// surface: one more screen's worth of formatters there would have taken the
// file well past the repo's 500-line ceiling. The settings helpers keep their
// home; everything the ops card alone needs lives here.
// =============================================================================

/**
 * The status the ops table shows for one row.
 *
 * A superset of the wire {@link FairUseStatus}: a row with a pending request
 * is reported by the backend with whatever enforcement status it happens to
 * have (often `warn` or `block`), but the admin's job on that row is to answer
 * the request — so "Request waiting" outranks the enforcement status in the
 * Status column, and the row is lifted out of the table's normal colouring.
 */
export type FairUseRowStatus =
  | "ok"
  | "warn"
  | "pending"
  | "downgrade"
  | "block"
  // Budget limits off (`fair_use.enabled=false`): the backend still classifies
  // each row by percent-of-cap, but nothing warned, downgraded or stopped
  // anyone. These two name the classification without claiming an action.
  | "near"
  | "over"

/**
 * Status shown for a row: a pending request wins over the enforcement status.
 *
 * `enforced` is the month's `enabled` flag. While it is false no one was
 * warned, moved to the fallback model or stopped, so `warn`/`downgrade` read
 * as "near" and `block` as "over" — the same rows the Near/Over counts select.
 */
export function fairUseRowStatus(row: FairUseOverviewRow, enforced = true): FairUseRowStatus {
  if (row.pending_request) return "pending"
  if (!enforced) {
    if (row.status === "block") return "over"
    if (row.status === "warn" || row.status === "downgrade") return "near"
  }
  return row.status
}

const ROW_STATUS_BADGE_VARIANT: Record<
  FairUseRowStatus,
  "success" | "secondary" | "warning" | "destructive"
> = {
  ok: "success",
  // Informational, not alarming: the user is inside their budget and has only
  // been told about it. Amber here would make three of five states amber.
  warn: "secondary",
  pending: "warning",
  downgrade: "warning",
  block: "destructive",
  near: "secondary",
  // Over budget but not stopped: worth a look, not an alarm.
  over: "warning",
}

/** Design-system badge variant for a row status. Tokens only — never a raw hue. */
export function fairUseRowBadgeVariant(
  status: FairUseRowStatus
): "success" | "secondary" | "warning" | "destructive" {
  return ROW_STATUS_BADGE_VARIANT[status]
}

const ROW_STATUS_PROGRESS_CLASS: Record<FairUseRowStatus, string> = {
  ok: "bg-secondary",
  warn: "bg-warning-ink",
  pending: "bg-warning-ink",
  downgrade: "bg-warning-ink",
  block: "bg-destructive-ink",
  near: "bg-warning-ink",
  over: "bg-warning-ink",
}

/**
 * Progress-bar fill for a row status, as a theme token class.
 *
 * Deliberately NOT {@link fairUseProgressIndicatorClass}: that one paints the
 * four-band user-surface palette in raw Tailwind hues (`bg-orange-500`), which
 * a customer theme cannot restyle. The ops table is a customer-visible admin
 * surface, so its colours come from the same tokens as its badges — and the
 * bar and the badge are then driven by ONE status, so they cannot disagree.
 */
export function fairUseRowProgressClass(status: FairUseRowStatus): string {
  return ROW_STATUS_PROGRESS_CLASS[status]
}

/**
 * Avatar initials for a row — at most two letters, upper-cased.
 *
 * Takes the identity half only, so the people table (whose rows may carry no
 * usage at all) renders the same avatar as the operations table.
 */
export function fairUseInitials(row: FairUsePersonIdentity): string {
  // An e-mail contributes only its local part: "johan.lind@x.se" is Johan
  // Lind, not "J. se".
  const source = row.profile?.name?.trim() || row.profile?.email?.trim().split("@")[0] || ""
  if (!source) return "?"
  const words = source.split(/[\s._-]+/u).filter(Boolean)
  if (words.length === 0) return "?"
  const letters =
    words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[words.length - 1][0]
  return letters.toLocaleUpperCase()
}

const monthLabelFormatters = new Map<string, Intl.DateTimeFormat>()

/** The backend month key for "now", UTC — same convention as getRecentMonthOptions. */
export function currentUtcYearMonth(now: Date = new Date()): string {
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`
}

/**
 * The denominator for "Showing N of M".
 *
 * A filtered overview is a truncated unpaged match set: `matched` is how many
 * users matched, not the month's `totals.users`. Using the month total would
 * present a 50-row slice of 80 matches as the complete filtered set.
 */
export function fairUseOpsShownTotal(args: {
  filter: FairUseOverviewFilter
  matched: number | null | undefined
  totalsUsers: number | null | undefined
  summaryUsers: number | undefined
  loadedCount: number
}): number {
  if (args.filter !== "all") {
    return typeof args.matched === "number" ? args.matched : args.loadedCount
  }
  return args.totalsUsers ?? args.summaryUsers ?? args.loadedCount
}

/** "2026-09" → "September 2026" in the viewer's locale. Falls back to the key. */
export function formatMonthLabel(yearMonth: string, locale: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(yearMonth)
  if (!match) return yearMonth
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1))
  try {
    let formatter = monthLabelFormatters.get(locale)
    if (!formatter) {
      formatter = new Intl.DateTimeFormat(locale, {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      })
      monthLabelFormatters.set(locale, formatter)
    }
    const label = formatter.format(date)
    return label.charAt(0).toLocaleUpperCase(locale) + label.slice(1)
  } catch {
    return yearMonth
  }
}

const AGE_UNITS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 60 * 60 * 1000],
  ["month", 30 * 24 * 60 * 60 * 1000],
  ["day", 24 * 60 * 60 * 1000],
  ["hour", 60 * 60 * 1000],
  ["minute", 60 * 1000],
]

/**
 * ISO timestamp → "2 hours ago" in the viewer's locale.
 *
 * Used for the age of a waiting request, which is the one thing that makes one
 * pending row more urgent than another. Returns `undefined` for an unparsable
 * value so the caller drops the fragment instead of printing "Invalid Date".
 */
export function formatRelativeAge(
  ts: string | null | undefined,
  locale: string,
  now: number = Date.now()
): string | undefined {
  if (!ts) return undefined
  const then = new Date(ts).getTime()
  if (Number.isNaN(then)) return undefined
  const elapsed = Math.max(0, now - then)
  try {
    const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" })
    for (const [unit, ms] of AGE_UNITS) {
      if (elapsed >= ms) return formatter.format(-Math.floor(elapsed / ms), unit)
    }
    return formatter.format(0, "minute")
  } catch {
    return undefined
  }
}

const sekFormatters = new Map<string, Intl.NumberFormat>()

/**
 * A whole-krona amount, grouped for the locale ("1 842").
 *
 * Whole kronor on purpose: the ops table compares budgets at a glance, and
 * two decimals on every cell ({@link formatSek}'s job on the user surface,
 * where the exact figure matters) turned the column into noise.
 */
export function formatSekAmount(value: number | null | undefined, locale: string): string {
  if (value == null || !Number.isFinite(value)) return "-"
  try {
    let formatter = sekFormatters.get(locale)
    if (!formatter) {
      formatter = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 })
      sekFormatters.set(locale, formatter)
    }
    return formatter.format(value)
  } catch {
    return String(Math.round(value))
  }
}

/** "241 / 250 SEK · 96 %" — one cell for spend, budget and share of it. */
export function formatBudgetUsage(row: FairUseOverviewRow, locale: string): string {
  const spent = formatSekAmount(row.cost_sek, locale)
  const budget = formatSekAmount(row.cap_sek, locale)
  return `${spent} / ${budget} SEK · ${roundPercent(row.percent)} %`
}

/** One entry of a row's "···" menu. */
export type FairUseRowAction =
  | { kind: "top-up"; sek: number }
  | { kind: "revoke"; grantId: string; sek: number; ts: string }

/**
 * The actions offered on a non-pending row.
 *
 * Revoking rewrites an invoiced line, so it is platform-only — and gating it
 * here rather than in the menu's JSX is what lets the rule be tested without
 * driving a portalled dropdown.
 */
export function fairUseRowActions(args: {
  row: FairUseOverviewRow
  isPlatformAdmin: boolean
  blockCapSek: number
}): FairUseRowAction[] {
  const actions: FairUseRowAction[] = [{ kind: "top-up", sek: args.blockCapSek }]
  if (!args.isPlatformAdmin) return actions
  for (const grant of args.row.grants ?? []) {
    actions.push({
      kind: "revoke",
      grantId: grant.id,
      sek: Number.isFinite(grant.sek) ? grant.sek : grant.blocks * args.blockCapSek,
      ts: grant.ts,
    })
  }
  return actions
}

/**
 * Client-side name/e-mail search over the rows already loaded.
 *
 * Deliberately client-side: the server filter is the chip row (a real,
 * paginated query), while this is a "find the person I'm looking at" narrowing
 * of what is on screen — making it a round trip would reset the pagination on
 * every keystroke.
 */
export function searchFairUseRows<Row extends FairUsePersonIdentity>(
  rows: Row[],
  query: string
): Row[] {
  const needle = query.trim().toLocaleLowerCase()
  if (!needle) return rows
  return rows.filter((row) => {
    const haystack = [row.profile?.name, row.profile?.email, row.profile?.department, row.user]
    return haystack.some((value) => value?.toLocaleLowerCase().includes(needle))
  })
}

/**
 * The top-up size in SEK the ops card should quote.
 *
 * Resolution order matters: a delegated admin may resolve requests WITHOUT
 * read access to the commercial config, so the summary and the overview page
 * are asked first and the config singleton is the last resort. The final
 * fallback keeps the button from reading "Approve +undefined SEK" while the
 * first response is still in flight.
 */
export const FAIR_USE_FALLBACK_BLOCK_CAP_SEK = 50

export function resolveFairUseBlockCapSek(sources: Array<number | null | undefined>): number {
  for (const value of sources) {
    if (typeof value === "number" && Number.isFinite(value) && value > 0) return value
  }
  return FAIR_USE_FALLBACK_BLOCK_CAP_SEK
}

const grantDateFormatters = new Map<string, Intl.DateTimeFormat>()

/** ISO timestamp → "4 Sep", for naming one top-up in the row menu. */
export function formatGrantDate(ts: string, locale: string): string {
  const date = new Date(ts)
  if (Number.isNaN(date.getTime())) return ts
  try {
    let formatter = grantDateFormatters.get(locale)
    if (!formatter) {
      formatter = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" })
      grantDateFormatters.set(locale, formatter)
    }
    return formatter.format(date)
  } catch {
    return date.toISOString().slice(0, 10)
  }
}

const ROW_STATUS_COPY: Record<FairUseRowStatus, { key: string; defaultValue: string }> = {
  ok: { key: "adminDashboard.fairUse.ops.status.ok", defaultValue: "OK" },
  warn: { key: "adminDashboard.fairUse.ops.status.warn", defaultValue: "Warned" },
  pending: { key: "adminDashboard.fairUse.ops.status.pending", defaultValue: "Request waiting" },
  downgrade: {
    key: "adminDashboard.fairUse.ops.status.downgrade",
    defaultValue: "Fallback model",
  },
  block: { key: "adminDashboard.fairUse.ops.status.block", defaultValue: "Stopped" },
  near: { key: "adminDashboard.fairUse.ops.status.nearUnenforced", defaultValue: "Near budget" },
  over: { key: "adminDashboard.fairUse.ops.status.overUnenforced", defaultValue: "Over budget" },
}

/**
 * The i18n key and English fallback for a row status.
 *
 * An explicit table rather than an interpolated key with the wire value as its
 * default: a missing catalogue entry would otherwise print the raw enum
 * ("downgrade") in the Status column instead of the word the admin was
 * promised ("Fallback model").
 */
export function fairUseRowStatusCopy(status: FairUseRowStatus): {
  key: string
  defaultValue: string
} {
  return ROW_STATUS_COPY[status]
}
